import React, { useEffect, useState } from "react";
import {
    createHourType,
    updateHourType,
    getHourType,
    destroyHourType,
} from "../../../../../_api/HourType";
import { useNavigate, useParams } from "react-router-dom";
import { InputForm } from "../../../../../_helpers/components/Forms/InputLabel";
import { Button } from "../../../../../_helpers/components/Button";
import toast from "react-hot-toast";
import { Toggle } from "../../../../../_helpers/components/Forms/ToggleButton";
import { ColorPicker } from "../../../../../_helpers/components/Forms/ColorPicker";
import { BackButton } from "../../../../../_helpers/components/BackButton";

const AddHoursType = () => {
    const navigate = useNavigate();
    const { ht_id } = useParams();
    const [name, setName] = useState("");
    const [isWork, setIsWork] = useState(false);
    const [isAbsence, setIsAbsence] = useState(false);
    const [isOnSite, setIsOnSite] = useState(false);
    const [isWithValidation, setIsWithValidation] = useState(0);
    const [requiresClocking, setRequiresClocking] = useState(false);
    const [abbreviation, setAbbreviation] = useState("");
    const [bgColor, setBgColor] = useState("#FFFFFF");
    const [mandatoryComment, setMandatoryComment] = useState(false);
    const [colorPickerVisible, setColorPickerVisible] = useState(false);
    const [darkText, setDarkText] = useState(true);
    const [isLoading, setIsLoading] = useState(true);
    const [isSaving, setIsSaving] = useState(false);
    const [isDeleting, setIsDeleting] = useState(false);

    const loadHourType = async () => {
        setIsLoading(true);
        if (ht_id) {
            await getHourType(ht_id)
                .then((resp) => {
                    setName(resp.data.name);
                    setIsWork(resp.data.is_work);
                    setIsAbsence(resp.data.is_absence);
                    setIsOnSite(resp.data.is_on_site);
                    setIsWithValidation(resp.data.is_with_validation);
                    setRequiresClocking(resp.data.requires_clocking);
                    setAbbreviation(resp.data.abbreviation);
                    setBgColor(resp.data.bg_color);
                    setMandatoryComment(resp.data.mandatory_comment);
                    changeColors(resp.data.bg_color);
                })
                .catch((resp) => {
                    // Accés refusé ?
                    toast("Accès refusé.");
                    navigate("/company/time");
                });
        }
        setIsLoading(false);
    };

    useEffect(() => {
        loadHourType();
    }, [ht_id]);

    const AddHoursType = () => {
        const payload = {
            name: name,
            is_work: isWork,
            is_absence: isAbsence,
            is_on_site: isOnSite,
            is_with_validation: isWithValidation,
            requires_clocking: requiresClocking,
            abbreviation: abbreviation,
            bg_color: bgColor,
            mandatory_comment: mandatoryComment,
        };
        setIsSaving(true);
        if (ht_id) {
            updateHourType(ht_id, payload).then(() => {
                toast("Modification enregistrée.");
                navigate("/company/time");
            }).finally(() => {
                setIsSaving(false);
            });
        } else {
            createHourType(payload)
                .then((request) => {
                    toast(
                        "Le type d'heure \"" + payload.name + '" a été créé.'
                    );
                    navigate("/company/time");
                })
                .catch(() => {
                    toast(
                        "Une erreur est survenue lors de la création du type d'heure."
                    );
                })
                .finally(() => {
                    setIsSaving(false);
                });
        }
    };

    const deleteHourType = () => {
        if (ht_id) {
            setIsDeleting(true);
            destroyHourType(ht_id).then(() => {
                toast("Le type d'heure \"" + name + '" a été supprimé.');
                navigate("/company/time");
            }).finally(() => {
                setIsDeleting(false);
            });
            return;
        }
        navigate("/company/time");
    };

    const changeColors = (color) => {
        setBgColor(color);
        const r = Number("0x" + color.substring(1, 3));
        const g = Number("0x" + color.substring(3, 5));
        const b = Number("0x" + color.substring(5, 7));
        setDarkText(0.3 * r + 0.59 * g + 0.11 * b >= 128);
        setColorPickerVisible(false);
    };

    return (
        <div className="min-h-full bg-gray-50">
            {/* Header */}
            <div className="bg-white border-b border-gray-200 shadow-sm">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
                    <div className="flex items-center justify-between flex-wrap gap-4">
                        <div className="flex items-center space-x-4">
                            <BackButton
                                onClick={() => navigate("/company/time")}
                                title="Retour"
                            />
                            <div>
                                <h1 className="text-3xl font-bold text-gray-900">
                                    {ht_id ? "Modifier" : "Ajouter"} un type d'heure
                                </h1>
                                <p className="mt-2 text-sm text-gray-600">
                                    {ht_id ? "Modifiez" : "Créez"} un type d'heure pour votre planning
                                </p>
                            </div>
                        </div>
                        <div className="flex flex-wrap gap-2">
                            {ht_id && (
                                <Button
                                    action={() => deleteHourType()}
                                    type={"red"}
                                    name={"Supprimer"}
                                    loading={isDeleting}
                                />
                            )}
                            <Button
                                action={() => AddHoursType()}
                                name={ht_id ? "Modifier" : "Ajouter"}
                                loading={isSaving}
                            />
                        </div>
                    </div>
                </div>
            </div>

            {/* Contenu principal */}
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
                {isLoading ? (
                    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                        <div className="flex items-center justify-center py-12">
                            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-app-primary"></div>
                        </div>
                    </div>
                ) : (
                    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                        <div className="flex gap-4">
                            <InputForm
                                name="Nom"
                                value={name}
                                placeholder="Congés payés"
                                type="text"
                                set={setName}
                                required={true}
                                classNameWrapper="w-96"
                            />
                            <InputForm
                                name="Abréviation"
                                value={abbreviation}
                                placeholder="CP"
                                type="text"
                                set={setAbbreviation}
                                max={5}
                                required={false}
                            />
                            <div>
                                <label className="text-left block uppercase tracking-wide text-app-primary text-xs font-bold mb-2">Couleur de fond</label>
                                <div
                                    className="h-10 w-40 mt-2"
                                    onMouseLeave={() =>
                                        setColorPickerVisible(false)
                                    }
                                >
                                    <div
                                        className="border bg-slate-400 h-10 py-2 px-3 rounded-md"
                                        style={{ backgroundColor: bgColor }}
                                        onMouseEnter={() =>
                                            setColorPickerVisible(true)
                                        }
                                        onClick={() =>
                                            setColorPickerVisible(true)
                                        }
                                    >
                                        <div
                                            className={
                                                darkText
                                                    ? "text-black/60"
                                                    : "text-white/60"
                                            }
                                        >
                                            Exemple
                                        </div>
                                    </div>
                                    {colorPickerVisible && (
                                        <ColorPicker
                                            setColor={changeColors}
                                            closeModal={() => setColorPickerVisible(false)}
                                            full={false}
                                        />
                                    )}
                                </div>
                            </div>
                        </div>

                        <div className="w-full lg:w-1/2 grid grid-cols-2 justify-between">
                            <label>Est-ce qu'il faut une validation ?</label>
                            <Toggle
                                enabled={isWithValidation}
                                setEnabled={setIsWithValidation}
                                name={""}
                                required={false}
                            />
                            <label>Compté dans le temps de travail</label>
                            <Toggle
                                enabled={isWork}
                                setEnabled={setIsWork}
                                name={""}
                                required={false}
                            />
                            <label>Est une absence</label>
                            <Toggle
                                enabled={isAbsence}
                                setEnabled={setIsAbsence}
                                name={""}
                                required={false}
                            />
                            <label>Présence sur site</label>
                            <Toggle
                                enabled={isOnSite}
                                setEnabled={setIsOnSite}
                                name={""}
                                required={false}
                            />
                            <label>Pointé</label>
                            <Toggle
                                enabled={requiresClocking}
                                setEnabled={setRequiresClocking}
                                name={""}
                                required={false}
                            />
                            <label>Commentaire obligatoire</label>
                            <Toggle
                                enabled={mandatoryComment}
                                setEnabled={setMandatoryComment}
                                name={""}
                                required={false}
                            />
                        </div>

                    </div>
                )}
            </div>
        </div>
    );
};

export default AddHoursType;
